import type { Move } from '../../../schemas/move';

/**
 * Mosse iniziali del Warlock (manuale V1, rr. 5842-5985).
 *
 * - **3 mosse automatiche** (`type: 'starting'`):
 *   - *La Fortuna degli Sciocchi*
 *   - *Deflagrazione Occulta* (mossa-tiro+CAR con etichette dinamiche per ogni invocazione,
 *     description-only)
 *   - *Contratto* (mossa-config con benefici + obbligo, multi-gruppo description-only — FI-18)
 *
 * - **3 mosse di scelta** (`pickCount: 1`): *Vincolo della Catena*, *Vincolo del Tomo*,
 *   *Vincolo della Lama*. Manuale r. 5899: «*Inoltre, scegli un Vincolo:*».
 *
 * - **2 mosse "casting" condizionate al Vincolo del Tomo** (`type: 'starting'`, **FI-38**):
 *   - *Mana* (`warlock.mana`, tag `'mana'` data-driven per dedup cross-class con Mago/Chierico)
 *   - *Lanciare Incantesimi del Patrono* (`warlock.lanciare-incantesimi-del-patrono`, roll+CAR)
 *
 *   Queste 2 mosse vengono acquisite automaticamente via `MoveSchema.bundles` sul
 *   *Vincolo del Tomo* (anchor del bundle). Manuale r. 5908: «Se prendi questa mossa,
 *   ottieni anche le seguenti mosse: *Mana* e *Lanciare Incantesimi del Patrono*».
 *
 * **Pattern *Vincolo del Tomo* come anchor del bundle** (FI-38 Opz. C):
 *
 * Il PG Warlock che sceglie *Vincolo del Tomo* è casteggiante. Il Tomo è
 * **anchor del bundle** (manuale r. 5908): espande automaticamente le 2 mosse
 * *Mana* + *LI del Patrono* nel `Character.moves`. La mossa di lancio
 * `warlock.lanciare-incantesimi-del-patrono` è il **trigger della spell-list
 * `warlock-native`** + **container delle `grantsSpells` setup-L1** (Suppliche
 * L0 auto + 2 spell L1 pick) — post-Opz. C simmetrico a Mago/Chierico.
 *
 * Il Paladino-Sovrannaturale-Tomo riceve `warlock.vincolo-del-tomo` come
 * granted cross-class (via `paladino.ordine-sovrannaturale.grantsMoveChoice
 * from-choice-mapping`); il bundle del Tomo propaga ricorsivamente Mana
 * e LI del Patrono — simmetria completa con il Warlock vero.
 *
 * **`prerequisites.anyOf` cross-class verso Paladino (FI-27)**: i 3 Vincoli hanno
 * «Richiede: *Contratto* o *Ordine Sovrannaturale*» — `anyOf` strutturato verso
 * `paladino.ordine-sovrannaturale`. Ora che il Paladino è già in repo (9/12), il
 * cross-class ref si risolve naturalmente.
 *
 * **D5 — Archetipo Warlock-Nano (immunità veleni)**: l'archetipo Nano ottiene
 * "immunità ai veleni" gratis dalle opzioni del *Contratto*. Description-only nel testo
 * del *Contratto* (paragrafo che lista i 12 benefici). Niente `extraTags` schema.
 */

const laFortunaDegliSciocchi: Move = {
  id: 'warlock.la-fortuna-degli-sciocchi',
  name: 'La Fortuna degli Sciocchi',
  type: 'starting',
  classId: 'warlock',
  trigger: 'Quando subisci danno',
  description: 'Prendi +1 al prossimo tiro di *sfidare il pericolo*.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const deflagrazioneOcculta: Move = {
  id: 'warlock.deflagrazione-occulta',
  name: 'Deflagrazione Occulta',
  type: 'starting',
  classId: 'warlock',
  trigger: 'Quando invochi la furia del tuo patrono contro un avversario',
  description:
    'Scegli due etichette *diverse* tra: *lunga*, *vicino*, *stordente*, *impatto* e *1 perforante*. Poi tira+**CAR**. Se non scegli alcuna etichetta di portata, quella base è *corta*.\n\nCon 10+, infliggi al bersaglio 1d6 danni con etichetta *di magia materializzata* (è magia, ma si manifesta in modo fisico). Con 7-9, lo stesso, ma devi scegliere una opzione tra le seguenti:\n\n- infliggi -1 danni e il GM rimuove una delle etichette che hai scelto, che non sia di portata;\n- subisci -1 continuato a usare *Deflagrazione Occulta* fintanto che non ti *accampi*;\n- attiri attenzioni indesiderate, ti esponi al pericolo o metti te stesso in difficoltà.\n\nPuoi anche decidere di spendere 1 presa di *Mana* (e non più di una!) per infliggere +1d6 danni, ma, comunque vada, usare questa mossa *non* equivale a *lanciare incantesimi*.\n\n*La scelta delle 2 etichette avviene a ogni invocazione, non al momento della presa della mossa.*',
  roll: {
    stat: 'carisma',
    on10plus: { text: '1d6 danni con etichetta *di magia materializzata*.' },
    on7to9: { text: '1d6 danni *di magia materializzata*, ma scegli 1 condizione dalla lista.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const contratto: Move = {
  id: 'warlock.contratto',
  name: 'Contratto',
  type: 'starting',
  classId: 'warlock',
  trigger: 'Alla creazione del personaggio (configurazione del Patrono)',
  description:
    'Hai firmato un *Contratto* con una forza ultraterrena (demoniaca, fatata, o altro). Scegli 2 benefici e 1 obbligo, e descrivi il patrono.\n\nSe lasci passare troppo tempo senza sforzarti sinceramente per assolvere ai tuoi obblighi, prendi -1 continuato a tutti i tiri fino a quando non torni nelle grazie del tuo patrono (il GM ti dirà come). *Non prendere* questa mossa se hai già *Faro di Devozione* (Paladino).\n\n*Archetipo Nano: «immunità ai veleni» è gratis (in aggiunta ai 2 benefici scelti normalmente).*',
  tags: [],
  prerequisites: [],
  replaces: [],
  // FI-18: 2 choiceGroups (benefici + obbligo) + 1 narrativeField (descrizione patrono).
  choiceGroups: [
    {
      id: 'benefici',
      label: 'Benefici (scegli 2)',
      description: 'Scegli i due benefici che hai ricevuto dal patrono.',
      options: [
        { id: 'liberta-fame-sete-sonno', label: 'Libertà da fame, sete e sonno' },
        { id: 'aspetto-impeccabile', label: 'Aspetto e modi impeccabili' },
        {
          id: 'immunita-malattie',
          label: 'Immunità a malattie, debolezza e vecchiaia',
        },
        {
          id: 'lingua-arcana',
          label: 'Conoscenza di una lingua arcana dimenticata',
        },
        {
          id: 'immunita-possessione',
          label: "Immunità alle possessioni e ad effetti sull'anima",
        },
        { id: 'immunita-controllo-mentale', label: 'Immunità al controllo mentale' },
        {
          id: 'percezione-piano-patrono',
          label: 'Percezione del piano di esistenza del patrono',
        },
        { id: 'visione-notturna', label: 'Visione notturna' },
        { id: 'respirare-sottacqua', label: "Capacità di respirare sott'acqua" },
        { id: 'invulnerabilita-calore', label: 'Invulnerabilità al calore e alle fiamme' },
        { id: 'invulnerabilita-freddo', label: 'Invulnerabilità al freddo' },
        {
          id: 'immunita-veleni',
          label: 'Immunità ai veleni',
          // FI-32: gratis per archetipo Nano del Warlock (non conta nel
          // pickCount=2 del gruppo `benefici`). Disponibile anche agli altri
          // archetipi (li costa 1 dei 2 benefici scelti).
          freeForArchetype: 'nano',
        },
      ],
      pickCount: 2,
    },
    {
      id: 'obbligo',
      label: 'Obbligo (scegli 1)',
      description: 'Il vincolo che il patrono ti ha imposto.',
      options: [
        {
          id: 'adorazione',
          label: 'Adorazione',
          description: 'Porta altri al servizio del tuo patrono.',
        },
        {
          id: 'segretezza',
          label: 'Segretezza',
          description: "Nascondi l'identità del tuo patrono a ogni costo.",
        },
        {
          id: 'competizione',
          label: 'Competizione',
          description: 'Lavora per ostacolare i nemici del tuo patrono.',
        },
        {
          id: 'sacrificio',
          label: 'Sacrificio',
          description: 'Distruggi oggetti di potere come dono al tuo patrono.',
        },
      ],
      pickCount: 1,
    },
  ],
  narrativeFields: [
    {
      id: 'descrizione-patrono',
      label: 'Descrizione del Patrono',
      placeholder:
        'Aspetto, natura (demoniaca/fatata/altra), personalità e obiettivi noti ai mortali. Da definire con il GM.',
      required: true,
      multiline: true,
    },
  ],
};

// Helper per i 3 Vincoli: tutti hanno «Richiede: *Contratto* o *Ordine Sovrannaturale*»
// (manuale rr. 5903, 5910, 5918). Per il PG L1 Warlock il prereq è sempre soddisfatto
// perché *Contratto* è iniziale auto. Il `anyOf` strutturato cross-class verso il Paladino
// supporta il caso Paladino-Sovrannaturale (chiusura FI-27).
//
// **NB**: ritorniamo un nuovo oggetto a ogni invocazione (non `as const`) per
// evitare il problema readonly→mutable di Zod su `PrerequisiteSchema`.
function vincoloPrereqAnyOf(): { anyOf: string[] } {
  return { anyOf: ['warlock.contratto', 'paladino.ordine-sovrannaturale'] };
}

const vincoloDellaCatena: Move = {
  id: 'warlock.vincolo-della-catena',
  name: 'Vincolo della Catena',
  type: 'starting',
  classId: 'warlock',
  trigger:
    'Quando aiuti qualcuno (PG o PNG) in un modo che sia per lui significativo e riconosciuto',
  description:
    "Lo leghi con un patto suggellato dal tuo patrono che lo obbliga a ripagarti in futuro. Puoi usare questo credito come leva per *parlamentare* con un PNG. Se lo fai, considera di aver ottenuto un 10+ senza bisogno di tirare, ma il debito è stato saldato.\n\nSe invece si tratta di un PG, puoi chiedergli un piccolo favore, di rispondere sinceramente a una domanda o di aiutarti a fare qualcosa. Se lo fa, il debito è pagato. Se si rifiuta, *lui stesso* deve tirare+**il numero di Legami che ha con te**. Con 10+, non fa ciò che gli hai chiesto, ma il suo debito con te rimane. Con 7-9, resiste, ma con un costo: ti dovrà un favore in più di prima. Con 6-, resiste, ma ciò lo rende debole: la prossima volta che lo ostacoli con la mossa *aiutare o ostacolare* ottieni un 10+ senza tirare.\n\nLa suddetta si applica a qualsivoglia tipo di essere (PG o PNG), a patto che: a) l'aiuto sia stato effettivamente utile al beneficiario e b) detto beneficiario riconosca, esplicitamente o nei fatti, di aver ottenuto vantaggi dai tuoi servigi.",
  tags: [],
  prerequisites: [vincoloPrereqAnyOf()],
  replaces: [],
};

const mana: Move = {
  id: 'warlock.mana',
  name: 'Mana',
  type: 'starting',
  classId: 'warlock',
  trigger:
    'Quando ti *accampi* e ripassi gli incantesimi che conosci, preghi la tua divinità o rinnovi i reciproci accordi con il tuo Patrono',
  description:
    'Al risveglio perdi tutte le prese di *Mana* che hai e ne prendi un numero pari al tuo più alto livello da Incantatore +1 (massimo 11).\n\nIl *Mana* è uno solo: se hai già preso questa mossa come Chierico o come Mago, non puoi prenderla di nuovo!',
  // **FI-38**: tag `'mana'` data-driven per il dedup cross-class nel bundle
  // expansion (manuale V1 r. 1133: «Mana è una riserva unica»). Coerente
  // con `chierico.mana` + `mago.mana` (D7-bundle).
  tags: ['mana'],
  prerequisites: [],
  replaces: [],
};

const lanciareIncantesimiDelPatrono: Move = {
  id: 'warlock.lanciare-incantesimi-del-patrono',
  name: 'Lanciare Incantesimi del Patrono',
  type: 'starting',
  classId: 'warlock',
  trigger: 'Quando vuoi lanciare un incantesimo contenuto nel tuo Tomo',
  description:
    "Se si tratta di una supplica, non importa che tiri: la lanci e basta. Altrimenti, se il tuo livello da Warlock e le tue prese di *Mana* residue sono almeno pari al livello dell'incantesimo che vuoi lanciare, tira+**CAR** (nota che le prese di *Mana* le stai solo rischiando, non spendendo!). Con un 10+, l'incantesimo viene lanciato con successo. Con 7-9, lo stesso, ma devi anche scegliere un'opzione:\n\n- attiri attenzioni indesiderate, ti esponi al pericolo o metti te stesso in difficoltà. Il GM ti dirà come;\n- il lancio dell'incantesimo logora il legame con il patrono: prendi -1 continuato a *Lanciare Incantesimi del Patrono* finché non usi di nuovo la mossa *Mana*. Se scegli questa opzione più volte, il malus si somma;\n- spendi prese di *Mana* pari al livello dell'incantesimo lanciato.\n\nRicorda che mantenere attivo un incantesimo con effetti continuati potrebbe causare una penalità ai tuoi tiri di *lanciare incantesimi*.",
  roll: {
    stat: 'carisma',
    on10plus: { text: "L'incantesimo viene lanciato con successo." },
    on7to9: {
      text: "Lanci comunque l'incantesimo, ma scegli un'opzione: attenzioni indesiderate / -1 continuato fino a prossimo Mana / spendi prese di Mana pari al livello dell'incantesimo.",
    },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
  // **FI-38 Opz. C revisione Q2/Q4 (2026-05-14)**: spell-grant setup-L1
  // del Tomo Warlock spostate qui (mossa di casting), simmetricamente a
  // `mago.lanciare-incantesimi-arcani` e `chierico.lanciare-incantesimi-divini`.
  // Prima di Opz. C vivevano su `warlock.vincolo-del-tomo` (Q2-A FI-38
  // base, preservava Paladino-Sovrannaturale-Tomo); ora il pattern
  // uniforme è "grant su mossa di lancio" + Paladino-Sovrannaturale-Tomo
  // riceve la mossa di lancio via bundle del Tomo (granted cross-class).
  //  - Grant 1: tutte le Suppliche L0 del Warlock auto-aggiunte
  //    (`all-of-level: 0` su own-class)
  //  - Grant 2: il giocatore sceglie 2 spell L1 dalla lista del Warlock
  grantsSpells: [
    {
      trigger: 'on-take',
      levelRule: { kind: 'all-of-level', level: 0 },
      source: { kind: 'own-class' },
    },
    {
      trigger: 'on-take',
      count: 2,
      levelRule: { kind: 'fixed', level: 1 },
      source: { kind: 'own-class' },
    },
  ],
};

const vincoloDelTomo: Move = {
  id: 'warlock.vincolo-del-tomo',
  name: 'Vincolo del Tomo',
  type: 'starting',
  classId: 'warlock',
  trigger: 'Sempre (effetto continuo: possedere il Tomo)',
  description:
    'Il tuo *Contratto* o la tua devozione ti conferiscono un Tomo su cui il tuo patrono ha impresso delle formule magiche. Il Tomo ha peso 1 e contiene tutte le suppliche e due incantesimi di 1° livello a tua scelta dalla lista del Warlock. Se il Tomo dovesse andare distrutto o perduto, puoi sempre implorare il tuo patrono di dartene uno nuovo. Il GM ti dirà cosa devi fare.\n\nSe prendi questa mossa, ottieni anche le mosse *Mana* e *Lanciare Incantesimi del Patrono*.',
  tags: [],
  prerequisites: [vincoloPrereqAnyOf()],
  replaces: [],
  // FI-18: descrizione del Tomo come narrativeField setup-L1.
  narrativeFields: [
    {
      id: 'descrizione-tomo',
      label: 'Descrizione del Tomo',
      placeholder:
        'Aspetto, materiali, glifi sulla copertina, odore, sensazione al tatto, eventuale incisione o nome…',
      required: true,
      multiline: true,
    },
  ],
  // **FI-38 (manuale V1 r. 5908)**: bundle all-inclusive Warlock. Anchor
  // = mossa di scelta del giocatore tra i 3 Vincoli (Q1=X). Mana dedupato
  // cross-class via tag `'mana'`. **Opz. C 2026-05-14**: `grantsSpells`
  // setup-L1 spostate su `warlock.lanciare-incantesimi-del-patrono`
  // (revisione Q2 → coerente con Mago/Chierico, grant su mossa di lancio).
  bundles: ['warlock.mana', 'warlock.lanciare-incantesimi-del-patrono'],
};

const vincoloDellaLama: Move = {
  id: 'warlock.vincolo-della-lama',
  name: 'Vincolo della Lama',
  type: 'starting',
  classId: 'warlock',
  trigger: "Sempre (effetto continuo sull'arma del patrono)",
  description:
    "Il tuo *Contratto* o la tua devozione ti conferiscono un'arma proveniente dal reame del tuo patrono. Configura la Tua Arma (tipologia + portata + 2 miglioramenti), descrivila ed eventualmente dalle un nome.\n\nQuando **impugni l'arma che hai ricevuto dal tuo patrono**, se il tuo dado di danno è d4 o d6, diventa d8.\n\n*Archetipo Mezz'orco: il miglioramento «truce» è gratis (in aggiunta ai 2 scelti).*",
  tags: [],
  prerequisites: [vincoloPrereqAnyOf()],
  replaces: [],
  // FI-18: configurazione arma identica a Arma Prescelta del Guerriero, ma con
  // 11 miglioramenti (10 standard + «truce» specifico per Mezz'orco-Warlock).
  choiceGroups: [
    {
      id: 'tipologia',
      label: 'Tipologia',
      description: 'Scegli la tipologia base della Tua Arma (peso 2).',
      options: [
        { id: 'spada', label: 'Spada' },
        { id: 'ascia', label: 'Ascia' },
        { id: 'martello', label: 'Martello' },
        { id: 'lancia', label: 'Lancia' },
        { id: 'mazza', label: 'Mazza' },
        { id: 'pugnale', label: 'Pugnale' },
        { id: 'guanti', label: 'Guanti da combattimento' },
      ],
      pickCount: 1,
    },
    {
      id: 'portata',
      label: 'Portata',
      description: 'Scegli la portata che meglio si adatta alla Tua Arma.',
      options: [
        { id: 'corta', label: 'Corta' },
        { id: 'media', label: 'Media' },
        { id: 'lunga', label: 'Lunga' },
      ],
      pickCount: 1,
    },
    {
      id: 'miglioramenti',
      label: 'Miglioramenti',
      description: 'Scegli due miglioramenti per la Tua Arma.',
      options: [
        {
          id: 'punte-e-uncini',
          label: 'Punte e uncini',
          description: '+1 danni, *brutale*, +1 peso.',
        },
        { id: 'affilata', label: 'Affilata', description: "Aggiungi l'etichetta *+2 perforante*." },
        {
          id: 'perfettamente-bilanciata',
          label: 'Perfettamente bilanciata',
          description: "Aggiungi l'etichetta *precisa*.",
        },
        { id: 'bordi-seghettati', label: 'Bordi seghettati', description: '+1 danni.' },
        {
          id: 'brilla-creatura',
          label: 'Brilla in presenza di un tipo di creatura',
          description: 'A tua scelta.',
        },
        { id: 'pesante', label: 'Pesante', description: '+1 danni, *impatto*, +1 peso.' },
        {
          id: 'sinistra',
          label: 'Sinistra',
          description: '+1 al prossimo tiro dopo aver ucciso un nemico.',
        },
        {
          id: 'antica',
          label: 'Antica',
          description: 'Vedi le mosse *Cimelio* e *Settimo Senso* del Guerriero.',
        },
        { id: 'versatile', label: 'Versatile', description: 'Scegli una portata aggiuntiva.' },
        { id: 'ben-costruita', label: 'Ben costruita', description: '-1 peso.' },
        {
          id: 'truce',
          label: 'Truce',
          description: '*brutale*.',
          // FI-32: gratis per archetipo Mezz'orco del Warlock (non conta nel
          // pickCount=2 del gruppo `miglioramenti`). Disponibile anche agli
          // altri archetipi (li costa 1 dei 2 miglioramenti scelti).
          freeForArchetype: 'mezzorco',
        },
      ],
      pickCount: 2,
    },
  ],
  narrativeFields: [
    {
      id: 'descrizione-arma',
      label: "Descrivi l'arma conferita dal patrono",
      placeholder:
        'Aspetto, materiali ultraterreni, glifi o ornamenti, manifestazione del patrono…',
      required: true,
      multiline: true,
    },
    {
      id: 'nome-arma',
      label: "Nome dell'arma (opzionale)",
      placeholder: 'es. Spaccacieli, Linguanera, Verità di Sangue…',
      required: false,
      multiline: false,
    },
  ],
};

/**
 * Mosse iniziali AUTOMATICHE (3): prese tutte al livello 1 senza scelta.
 */
export const warlockStartingAutoMoves: readonly Move[] = [
  laFortunaDegliSciocchi,
  deflagrazioneOcculta,
  contratto,
] as const;

/**
 * Mosse iniziali di SCELTA (3 Vincoli): il giocatore ne sceglie UNO al livello 1
 * (`pickCount: 1`).
 *
 * - *Vincolo della Catena* / *Vincolo della Lama*: niente casting (PG non casteggiante).
 * - *Vincolo del Tomo*: trigger semantico del casting `warlock-native` (D2) +
 *   anchor del bundle `[warlock.mana, warlock.lanciare-incantesimi-del-patrono]`
 *   (FI-38).
 */
export const warlockStartingChoiceMoves: readonly Move[] = [
  vincoloDellaCatena,
  vincoloDelTomo,
  vincoloDellaLama,
] as const;

/**
 * **FI-38** — Mosse "casting" condizionate al *Vincolo del Tomo*. Acquisite
 * automaticamente via `MoveSchema.bundles` quando il PG sceglie Tomo (manuale
 * V1 r. 5908). NON AUTO al L1 e NON in `startingChoiceGroups` — solo presenti
 * nel catalogo per il lookup `findMoveByIdGlobal` durante il bundle expansion.
 */
export const warlockBundleMoves: readonly Move[] = [mana, lanciareIncantesimiDelPatrono] as const;

/** Tutte le mosse iniziali (auto + scelta + bundle-obtained). */
export const warlockInitialMoves: readonly Move[] = [
  ...warlockStartingAutoMoves,
  ...warlockStartingChoiceMoves,
  ...warlockBundleMoves,
];
